import { useState } from "react";
import { CaretDown, CaretRight, Flag, ChatCircleText } from "@phosphor-icons/react";
import { MOVES, fmtClock, moveLabel } from "@/lib/format";

const RUBRIC = { 3: "Would read aloud", 2: "True, room knew it", 1: "Generic / wrong" };

const ScoreButtons = ({ value, onPick, itemKey }) => (
  <div className="flex gap-1.5" data-testid={`score-buttons-${itemKey}`}>
    {[3, 2, 1].map((n) => (
      <button
        key={n}
        onClick={() => onPick(value === n ? null : n)}
        title={RUBRIC[n]}
        className={`h-9 min-w-[36px] px-2 font-mono text-sm border transition-colors ${value === n ? "bg-[var(--o-fg)] text-[var(--o-bg)] border-[var(--o-fg)]" : "border-[var(--o-border)] text-[var(--o-muted)] hover:border-[var(--o-border-strong)] hover:text-[var(--o-fg)]"}`}
        data-testid={`score-${itemKey}-${n}`}
      >
        {n}
      </button>
    ))}
  </div>
);

export const ScoreItem = ({ item, mine, other, otherName, reveal, onScore }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(mine?.note || "");
  const disagree = reveal && mine && other && mine.score !== other.score;
  const color = MOVES[item.move]?.color;
  return (
    <div className={`border p-5 space-y-3 transition-colors ${disagree ? "border-[#F59E0B]/60" : "border-[var(--o-border)]"} ${mine ? "" : "bg-[var(--o-card)]"}`} data-testid={`score-item-${item.key}`}>
      <div className="flex flex-wrap items-center gap-2.5 text-xs">
        {item.kind === "flag" ? <Flag size={13} weight="fill" className="text-[#FF3B30]" /> : <ChatCircleText size={13} />}
        <span className="font-mono">{item.kind === "flag" ? `Flag ${item.id}` : `Answer #${String(item.n).padStart(2, "0")}`}</span>
        <span className="font-mono text-[var(--o-muted3)]">@ {fmtClock(item.t)}</span>
        {item.move && <span className="font-mono uppercase text-[10px] px-1.5 py-0.5 border" style={{ color, borderColor: `${color}55` }}>{moveLabel(item.move)}</span>}
        {item.kind === "answer" && <span className="font-mono text-[10px] text-[var(--o-muted2)]">usefulness {item.usefulness} · {item.shown ? "shown" : "held back"}</span>}
        {item.kind === "flag" && <span className="font-mono text-[10px] text-[var(--o-muted2)]">conf {item.confidence} · {item.status}</span>}
        <div className="ml-auto flex items-center gap-3">
          {reveal && other && <span className="font-mono text-[11px] text-[var(--o-muted)]" data-testid={`other-score-${item.key}`}>{otherName}: {other.score}</span>}
          <ScoreButtons value={mine?.score} itemKey={item.key} onPick={(v) => onScore(item.key, v, note)} />
        </div>
      </div>
      {item.kind === "answer" && <p className="font-mono text-[11px] text-[var(--o-muted3)]">Q · {item.question}</p>}
      <p className="text-[15px] leading-relaxed">{item.text || <span className="text-[var(--o-muted3)]">(no text — the Observer held back)</span>}</p>
      {item.missing_data && <p className="text-xs text-[#F59E0B]">Lacked data: {item.missing_data}</p>}
      <div className="flex flex-wrap items-center gap-4">
        {item.grounds?.length > 0 && (
          <button onClick={() => setOpen(!open)} className="flex items-center gap-1 font-mono text-[11px] text-[var(--o-muted2)] hover:text-[var(--o-fg)]" data-testid={`score-grounds-toggle-${item.key}`}>
            {open ? <CaretDown size={11} /> : <CaretRight size={11} />} Grounds · {item.grounds.length}
          </button>
        )}
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => mine && note !== (mine.note || "") && onScore(item.key, mine.score, note)}
          placeholder={mine ? "Note (optional)" : "Score first, then add a note"}
          disabled={!mine}
          className="flex-1 min-w-[180px] bg-transparent border-b border-[var(--o-border)] focus:border-[var(--o-fg)] py-1 text-xs outline-none disabled:opacity-40"
          data-testid={`score-note-${item.key}`}
        />
      </div>
      {open && (
        <ul className="space-y-1.5">
          {item.grounds.map((g, i) => <li key={i} className="font-mono text-[11px] text-[var(--o-muted)] pl-3 border-l border-[var(--o-border-strong)]">{g}</li>)}
        </ul>
      )}
    </div>
  );
};
